/**
 * Forgot password form component with two-step flow
 */

import { useState } from 'react';
import { z } from 'zod';
import { authService } from '@/services/auth.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

// Zod validation schemas
const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

const resetSchema = z
  .object({
    code: z.string().min(6, 'Verification code must be 6 digits').max(6),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type EmailFormData = z.infer<typeof emailSchema>;
type ResetFormData = z.infer<typeof resetSchema>;

interface ForgotPasswordFormProps {
  onBackToLoginClick?: () => void;
}

export function ForgotPasswordForm({ onBackToLoginClick }: ForgotPasswordFormProps) {
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');

  const [emailFormData, setEmailFormData] = useState<EmailFormData>({ email: '' });
  const [resetFormData, setResetFormData] = useState<ResetFormData>({
    code: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [emailErrors, setEmailErrors] = useState<Partial<Record<keyof EmailFormData, string>>>({});
  const [resetErrors, setResetErrors] = useState<Partial<Record<keyof ResetFormData, string>>>({});
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailFormData({ email: e.target.value });
    if (emailErrors.email) {
      setEmailErrors({});
    }
    if (serverError) {
      setServerError('');
    }
  };

  const handleResetChange =
    (field: keyof ResetFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setResetFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (resetErrors[field]) {
        setResetErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
      if (serverError) {
        setServerError('');
      }
    };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setEmailErrors({});
    setServerError('');

    const result = emailSchema.safeParse(emailFormData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof EmailFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof EmailFormData] = err.message;
        }
      });
      setEmailErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      await authService.forgotPassword({ email: emailFormData.email });
      setEmail(emailFormData.email);
      setStep('reset');
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'Failed to send reset code. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setResetErrors({});
    setServerError('');

    const result = resetSchema.safeParse(resetFormData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ResetFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof ResetFormData] = err.message;
        }
      });
      setResetErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword({
        email,
        code: resetFormData.code,
        newPassword: resetFormData.newPassword,
      });

      setShowSuccess(true);

      // Clear form
      setResetFormData({
        code: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Redirect to login after a delay
      setTimeout(() => {
        if (onBackToLoginClick) {
          onBackToLoginClick();
        }
      }, 3000);
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'Failed to reset password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>
          {step === 'email' ? 'Reset your password' : 'Enter verification code'}
        </CardTitle>
        <CardDescription>
          {step === 'email'
            ? 'Enter your email address and we will send you a verification code'
            : `We have sent a verification code to ${email}`}
        </CardDescription>
      </CardHeader>

      {step === 'email' ? (
        <form onSubmit={handleEmailSubmit}>
          <CardContent className="space-y-4">
            {serverError && (
              <Alert variant="destructive">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={emailFormData.email}
                onChange={handleEmailChange}
                disabled={isLoading}
                aria-invalid={!!emailErrors.email}
                aria-describedby={emailErrors.email ? 'email-error' : undefined}
              />
              {emailErrors.email && (
                <p id="email-error" className="text-sm text-destructive">
                  {emailErrors.email}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending code...
                </>
              ) : (
                'Send verification code'
              )}
            </Button>

            {onBackToLoginClick && (
              <button
                type="button"
                onClick={onBackToLoginClick}
                className="text-sm text-primary hover:underline"
                disabled={isLoading}
              >
                Back to login
              </button>
            )}
          </CardFooter>
        </form>
      ) : (
        <form onSubmit={handleResetSubmit}>
          <CardContent className="space-y-4">
            {showSuccess && (
              <Alert>
                <AlertDescription>
                  Password reset successful! Redirecting to login...
                </AlertDescription>
              </Alert>
            )}

            {serverError && (
              <Alert variant="destructive">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={resetFormData.code}
                onChange={handleResetChange('code')}
                disabled={isLoading || showSuccess}
                aria-invalid={!!resetErrors.code}
                aria-describedby={resetErrors.code ? 'code-error' : undefined}
              />
              {resetErrors.code && (
                <p id="code-error" className="text-sm text-destructive">
                  {resetErrors.code}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Check your email for the 6-digit verification code
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Create a new password"
                value={resetFormData.newPassword}
                onChange={handleResetChange('newPassword')}
                disabled={isLoading || showSuccess}
                aria-invalid={!!resetErrors.newPassword}
                aria-describedby={resetErrors.newPassword ? 'newPassword-error' : undefined}
              />
              {resetErrors.newPassword && (
                <p id="newPassword-error" className="text-sm text-destructive">
                  {resetErrors.newPassword}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your new password"
                value={resetFormData.confirmPassword}
                onChange={handleResetChange('confirmPassword')}
                disabled={isLoading || showSuccess}
                aria-invalid={!!resetErrors.confirmPassword}
                aria-describedby={resetErrors.confirmPassword ? 'confirmPassword-error' : undefined}
              />
              {resetErrors.confirmPassword && (
                <p id="confirmPassword-error" className="text-sm text-destructive">
                  {resetErrors.confirmPassword}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading || showSuccess}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting password...
                </>
              ) : (
                'Reset password'
              )}
            </Button>

            <button
              type="button"
              onClick={() => setStep('email')}
              className="text-sm text-primary hover:underline"
              disabled={isLoading || showSuccess}
            >
              Resend code
            </button>
          </CardFooter>
        </form>
      )}
    </Card>
  );
}
